// Card database plus GPU-side art streaming.
//
// The full set is 576 printings at 1030x1438; uploading all of them would be
// roughly 3.5 GB of VRAM, so albedo textures are loaded on demand and evicted
// least-recently-used. Foil masks are the opposite case -- there are only 17 of
// them and every card needs one immediately, so they are resident for the
// session.

import { MipmapGenerator, loadTexture, solidTexture } from '../gfx/texture.ts';
import type { CardVisual } from '../gfx/renderer.ts';
import { foilTier, type CardBack, type CardData, type Rarity } from './types.ts';

export interface LibraryOptions {
  /** How many card albedo textures to keep resident. */
  cacheSize?: number;
  /** Called as bulk assets load, for a progress display. */
  onProgress?: (loaded: number, total: number, label: string) => void;
}

interface CacheEntry {
  texture: GPUTexture;
  lastUsed: number;
}

export class CardLibrary {
  readonly cards: readonly CardData[];
  readonly byId: ReadonlyMap<string, CardData>;

  private readonly device: GPUDevice;
  /**
   * Shared so anything else that has to upload a texture -- the turn plaque,
   * for one -- reuses this blit pipeline rather than building a second one.
   */
  readonly mipmaps: MipmapGenerator;
  private readonly masks = new Map<string, GPUTexture>();
  private readonly fallbackMask: GPUTexture;
  private readonly placeholder: GPUTexture;
  private backTexture: GPUTexture;
  private readonly backs = new Map<CardBack, GPUTexture>();

  private readonly cache = new Map<string, CacheEntry>();
  private readonly inFlight = new Map<string, Promise<GPUTexture>>();
  private readonly cacheSize: number;
  private clock = 0;

  /** Invoked when a texture is evicted, so the renderer can drop its bind group. */
  onEvict: ((key: string) => void) | null = null;

  private constructor(
    device: GPUDevice,
    cards: CardData[],
    masks: Map<string, GPUTexture>,
    back: GPUTexture,
    backs: Map<CardBack, GPUTexture>,
    mipmaps: MipmapGenerator,
    cacheSize: number,
  ) {
    this.device = device;
    this.cards = cards;
    this.byId = new Map(cards.map((c) => [c.id, c]));
    this.mipmaps = mipmaps;
    this.masks = masks;
    this.backTexture = back;
    this.backs = backs;
    this.cacheSize = cacheSize;

    // A mid grey with no metal and a full art window: an unframed card still
    // lights correctly, it simply has no foil trim.
    this.fallbackMask = solidTexture(device, [0, 0, 255, 255], 'rgba8unorm');
    this.placeholder = solidTexture(device, [26, 28, 38, 255]);
  }

  static async load(device: GPUDevice, opts: LibraryOptions = {}): Promise<CardLibrary> {
    const report = opts.onProgress ?? (() => {});
    const mipmaps = new MipmapGenerator(device);

    report(0, 1, 'card data');
    const [cards, available] = await Promise.all([
      fetchJson<CardData[]>('/generated/cards.json'),
      fetchJson<string[]>('/generated/art-manifest.json'),
    ]);

    // Printings whose image never made it into the mirror would render as a
    // blank slab, so they are dropped from the playable set entirely.
    const have = new Set(available);
    const usable = cards.filter((c) => have.has(c.id));

    const frames = [...new Set(usable.map((c) => c.frame).filter((f): f is string => !!f))];
    const total = frames.length + 2;
    let done = 0;

    const masks = new Map<string, GPUTexture>();
    await Promise.all(
      frames.map(async (frame) => {
        // Masks are control data, not colour: they must not be sRGB-decoded.
        const tex = await loadTexture(device, `/generated/masks/${frame}.png`, mipmaps, {
          label: `mask:${frame}`,
          format: 'rgba8unorm',
        });
        masks.set(frame, tex);
        report(++done, total, `foil masks (${done}/${frames.length})`);
      }),
    );

    // The decks do not share a back. Runes are printed on a white stock and
    // legends and battlefields on black; everything in the Main Deck sits
    // between them, so a face-down pile says which pile it is before you read
    // the label under it.
    const backs = new Map<CardBack, GPUTexture>();
    await Promise.all(([
      ['dark', '/generated/cardback.png'],
      ['main', '/generated/cardback-main.png'],
      ['rune', '/generated/cardback-rune.png'],
    ] as const).map(async ([kind, url]) => {
      backs.set(kind, await loadTexture(device, url, mipmaps, { label: `card-back:${kind}` }));
    }));
    const back = backs.get('dark')!;
    report(++done, total, 'card backs');

    const backMask = await loadTexture(device, '/generated/masks/cardback.png', mipmaps, {
      label: 'mask:cardback',
      format: 'rgba8unorm',
    });
    masks.set('cardback', backMask);
    report(++done, total, 'ready');

    return new CardLibrary(device, usable, masks, back, backs, mipmaps, opts.cacheSize ?? 96);
  }

  get cardBack(): GPUTexture {
    return this.backTexture;
  }

  /**
   * The back this card is printed on.
   *
   * Runes come out of their own deck on white stock; legends and battlefields
   * are the black-backed singletons that start the game on the table. A pile
   * therefore says which pile it is at a glance, face down.
   */
  backFor(card: CardData): GPUTexture {
    const kind: CardBack = card.type === 'rune' ? 'rune'
      : card.type === 'legend' || card.type === 'battlefield' ? 'dark'
        : 'main';
    return this.backs.get(kind) ?? this.backTexture;
  }

  maskFor(card: CardData): GPUTexture {
    return (card.frame ? this.masks.get(card.frame) : undefined) ?? this.fallbackMask;
  }

  foilTierFor(card: CardData): number {
    return foilTier(card.rarity);
  }

  /**
   * A renderable handle for a card. Returns immediately with a placeholder if
   * the art is not resident yet; call again on a later frame to pick up the
   * real texture once it has streamed in.
   */
  visualFor(card: CardData): CardVisual {
    const entry = this.cache.get(card.id);
    if (entry) {
      entry.lastUsed = ++this.clock;
      return { key: card.id, albedo: entry.texture, mask: this.maskFor(card), back: this.backFor(card) };
    }
    void this.request(card);
    return {
      key: `${card.id}:pending`,
      albedo: this.placeholder,
      mask: this.maskFor(card),
      back: this.backFor(card),
    };
  }

  /** Same as `visualFor`, addressed by card id -- what the rules engine holds. */
  visualById(id: string): CardVisual {
    const card = this.byId.get(id);
    if (!card) {
      return {
        key: `missing:${id}`,
        albedo: this.placeholder,
        mask: this.fallbackMask,
        back: this.backTexture,
      };
    }
    return this.visualFor(card);
  }

  /** True once `visualFor` will hand back the real artwork. */
  isResident(id: string): boolean {
    return this.cache.has(id);
  }

  /** Streams a card's art in, resolving when it is resident. */
  async request(card: CardData): Promise<GPUTexture> {
    const existing = this.cache.get(card.id);
    if (existing) {
      existing.lastUsed = ++this.clock;
      return existing.texture;
    }

    let pending = this.inFlight.get(card.id);
    if (!pending) {
      pending = loadTexture(this.device, `/cards/${card.art}`, this.mipmaps, {
        label: `card:${card.id}`,
      })
        .then((texture) => {
          this.cache.set(card.id, { texture, lastUsed: ++this.clock });
          this.evict();
          return texture;
        })
        .finally(() => {
          this.inFlight.delete(card.id);
        });
      this.inFlight.set(card.id, pending);
    }
    return pending;
  }

  /** Warms the cache for a set of cards, e.g. a whole deck before a match. */
  async preload(cards: readonly CardData[], onProgress?: (n: number, total: number) => void): Promise<void> {
    let n = 0;
    // Bounded concurrency: the browser will happily open 500 sockets and then
    // deliver the first card last.
    const queue = [...cards];
    const workers = Array.from({ length: 8 }, async () => {
      for (;;) {
        const card = queue.shift();
        if (!card) return;
        try {
          await this.request(card);
        } catch (err) {
          console.warn(`[library] ${card.id} failed to load`, err);
        }
        onProgress?.(++n, cards.length);
      }
    });
    await Promise.all(workers);
  }

  private evict(): void {
    if (this.cache.size <= this.cacheSize) return;
    const entries = [...this.cache.entries()].sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const excess = this.cache.size - this.cacheSize;
    for (let i = 0; i < excess; i++) {
      const entry = entries[i];
      if (!entry) break;
      entry[1].texture.destroy();
      this.cache.delete(entry[0]);
      this.onEvict?.(entry[0]);
    }
  }

  // -- queries ---------------------------------------------------------------

  filter(pred: (c: CardData) => boolean): CardData[] {
    return this.cards.filter(pred);
  }

  ofRarity(rarity: Rarity): CardData[] {
    return this.cards.filter((c) => c.rarity === rarity);
  }

  /** A deterministic sample, so a given seed always yields the same spread. */
  sample(count: number, seed = 1, pred?: (c: CardData) => boolean): CardData[] {
    const pool = pred ? this.cards.filter(pred) : [...this.cards];
    let s = seed >>> 0 || 1;
    const rnd = () => {
      s ^= s << 13; s >>>= 0;
      s ^= s >> 17;
      s ^= s << 5; s >>>= 0;
      return s / 4294967296;
    };
    const out: CardData[] = [];
    const taken = new Set<number>();
    const n = Math.min(count, pool.length);
    while (out.length < n) {
      const i = Math.floor(rnd() * pool.length);
      if (taken.has(i)) continue;
      taken.add(i);
      out.push(pool[i]!);
    }
    return out;
  }

  destroy(): void {
    for (const e of this.cache.values()) e.texture.destroy();
    this.cache.clear();
    for (const m of this.masks.values()) m.destroy();
    this.masks.clear();
    this.fallbackMask.destroy();
    this.placeholder.destroy();
    this.backTexture.destroy();
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(
      `Missing ${url} (${res.status}). Run "npm run data:all" to build the asset pipeline.`,
    );
  }
  return res.json() as Promise<T>;
}
